import { useState } from "react";
import { Copy, Check, MessageCircle, Mail, Share2, Link2 } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function ShareDialog({ course, open, onOpenChange }) {
  const [copied, setCopied] = useState("");
  if (!course) return null;
  const url = `${window.location.origin}/courses/${course.id}`;
  const details = [
    course.title,
    course.institute?.name ? `By ${course.institute.name}` : null,
    `Location: ${course.city}, ${course.state}`,
    `Duration: ${course.duration} (${course.format})`,
    `Fee: ${inr(course.fee)}`,
    course.application_deadline ? `Apply by: ${course.application_deadline}` : null,
    (course.eligibility || []).length ? `Eligibility: ${course.eligibility.join(", ")}` : null,
    "",
    `Apply on Medlane: ${url}`,
  ].filter((l) => l !== null).join("\n");

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which); toast.success("Copied to clipboard");
      setTimeout(() => setCopied(""), 1500);
    } catch { toast.error("Could not copy"); }
  };
  const wa = `https://wa.me/?text=${encodeURIComponent(details)}`;
  const mail = `mailto:?subject=${encodeURIComponent("Course: " + course.title)}&body=${encodeURIComponent(details)}`;
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: course.title, text: details, url }); } catch { /* cancelled */ } }
    else copy(details, "details");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle>Share this course</DialogTitle>
          <DialogDescription>Send the full course details to colleagues or prospective students.</DialogDescription>
        </DialogHeader>
        <pre className="max-h-48 overflow-y-auto whitespace-pre-wrap rounded-xl border border-border bg-secondary/50 p-4 text-sm text-foreground">{details}</pre>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" data-testid="share-copy-link" onClick={() => copy(url, "link")}>
            {copied === "link" ? <Check className="mr-2 h-4 w-4" /> : <Link2 className="mr-2 h-4 w-4" />} Copy link
          </Button>
          <Button variant="outline" data-testid="share-copy-details" onClick={() => copy(details, "details")}>
            {copied === "details" ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />} Copy details
          </Button>
          <a href={wa} target="_blank" rel="noreferrer" data-testid="share-whatsapp">
            <Button className="w-full bg-[#25D366] text-white hover:bg-[#1da851]"><MessageCircle className="mr-2 h-4 w-4" /> WhatsApp</Button>
          </a>
          <a href={mail} data-testid="share-email">
            <Button variant="outline" className="w-full"><Mail className="mr-2 h-4 w-4" /> Email</Button>
          </a>
        </div>
        <Button variant="ghost" className="w-full" data-testid="share-native" onClick={nativeShare}>
          <Share2 className="mr-2 h-4 w-4" /> More sharing options
        </Button>
      </DialogContent>
    </Dialog>
  );
}
